import { useEffect, useId, useRef, useState } from 'react';

import { t } from '../i18n/messages';
import styles from './MobileMenu.module.css';

export interface NavLink {
  href: string;
  label: string;
}

/**
 * The narrow-width counterpart to the inline nav. Below 820px the links do not
 * fit beside the wordmark and the language selector, so they move in here —
 * including Contact, which otherwise pushes the header past a 375px viewport.
 */
export function MobileMenu({ links }: { links: NavLink[] }) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // Escape must put focus back where it came from, or a keyboard user is
      // dropped at the top of the document.
      toggleRef.current?.focus();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    // Closing on resize matters because the toggle itself disappears above the
    // breakpoint, which would otherwise strand the panel open with no way back.
    const onResize = () => {
      if (window.innerWidth > 820) setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  return (
    <div ref={containerRef}>
      <button
        ref={toggleRef}
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
        onClick={() => setOpen((previous) => !previous)}
      >
        {open ? (
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M3 6h14M3 10h14M3 14h14"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        )}
      </button>

      {open ? (
        <div className={styles.panel} id={panelId}>
          {links.map((link) => (
            <a key={link.href} href={link.href} onClick={() => setOpen(false)}>
              {link.label}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
